import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiInbox, FiSave, FiSend } from 'react-icons/fi';
import api from '../../config/api';
import LoadingButton from '../LoadingButton';
import { appAlert } from '../../utils/appDialog';
import { useAuth } from '../../context/AuthContext';

const SECURE_OPTIONS = [
  { value: 'starttls', label: 'STARTTLS (port 587)' },
  { value: 'ssl', label: 'SSL/TLS (port 465)' },
  { value: 'off', label: 'None (port 25)' },
];

const MODE_OPTIONS = [
  { value: 'both', label: 'Cloudflare Email Routing + webhook' },
  { value: 'cloudflare_email', label: 'Cloudflare Email Routing only' },
  { value: 'webhook', label: 'HTTP webhook only' },
];

/**
 * Settings → Email: Support inbox identity + optional dedicated SMTP.
 * Global SMTP (section above) is used unless dedicated SMTP is enabled.
 */
export default function SupportEmailConfig() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [testTo, setTestTo] = useState(user?.email || '');
  const [form, setForm] = useState({
    inbox_address: 'support@gobunnyy.com',
    from_name: 'GoBunnyy Support',
    inbound: {
      mode: 'both',
      accept_addresses_text: '',
      reject_unknown_to: false,
    },
    outbound: {
      enabled: true,
      use_dedicated_smtp: false,
      smtp: {
        host: '',
        port: 587,
        secure: 'starttls',
        username: '',
        password: '',
        from_email: '',
        from_name: '',
      },
    },
  });

  const { data, isLoading, isError, error } = useQuery(
    ['settings-support-email'],
    () => api.get('/permissions/support-email').then((r) => r.data),
    { refetchOnWindowFocus: false, retry: false }
  );

  useEffect(() => {
    if (!data) return;
    setForm({
      inbox_address: data.inbox_address || 'support@gobunnyy.com',
      from_name: data.from_name || 'GoBunnyy Support',
      inbound: {
        mode: data.inbound?.mode || 'both',
        accept_addresses_text:
          data.inbound?.accept_addresses_text ||
          (data.inbound?.accept_addresses || []).join(', '),
        reject_unknown_to: Boolean(data.inbound?.reject_unknown_to),
      },
      outbound: {
        enabled: data.outbound?.enabled !== false,
        use_dedicated_smtp: Boolean(data.outbound?.use_dedicated_smtp),
        smtp: {
          host: data.outbound?.smtp?.host || '',
          port: data.outbound?.smtp?.port ?? 587,
          secure: data.outbound?.smtp?.secure || 'starttls',
          username: data.outbound?.smtp?.username || '',
          password: '',
          from_email: data.outbound?.smtp?.from_email || '',
          from_name: data.outbound?.smtp?.from_name || '',
        },
      },
    });
  }, [data]);

  const saveMutation = useMutation(
    (body) => api.put('/permissions/support-email', body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['settings-support-email']);
        void appAlert('Support email configuration saved.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const testMutation = useMutation(
    () => api.post('/permissions/support-email/test', { to: testTo }),
    {
      onSuccess: (res) => void appAlert(res.data?.message || 'Test email sent.'),
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const handleSave = (e) => {
    e.preventDefault();
    const body = {
      inbox_address: form.inbox_address,
      from_name: form.from_name,
      inbound: {
        mode: form.inbound.mode,
        reject_unknown_to: form.inbound.reject_unknown_to,
        accept_addresses_text: form.inbound.accept_addresses_text,
      },
      outbound: {
        enabled: form.outbound.enabled,
        use_dedicated_smtp: form.outbound.use_dedicated_smtp,
        smtp: { ...form.outbound.smtp },
      },
    };
    if (!body.outbound.smtp.password?.trim()) delete body.outbound.smtp.password;
    saveMutation.mutate(body);
  };

  if (isLoading) {
    return <div className="loading">Loading support email configuration…</div>;
  }

  if (isError) {
    const status = error?.response?.status;
    if (status === 403) return null;
    return (
      <div className="support-email-settings" style={{ marginTop: '2rem' }}>
        <p className="form-hint">
          Could not load support email settings
          {status ? ` (${status})` : ''}:{' '}
          {error?.response?.data?.error || error?.message}
        </p>
      </div>
    );
  }

  const dedicated = form.outbound.use_dedicated_smtp;

  return (
    <div className="support-email-settings" style={{ marginTop: '2.5rem' }}>
      <h3 className="email-section-heading">
        <FiInbox aria-hidden /> Support desk email
      </h3>
      <p className="email-settings-intro" style={{ marginBottom: '1rem' }}>
        Configure the public support inbox (incoming) and how replies are sent (outgoing).
        By default, support uses the global SMTP above with From/Reply-To set to the support
        address. You can optionally use a dedicated SMTP mailbox. Templates for support actions
        remain in the notification list below.
      </p>

      <form onSubmit={handleSave} className="settings-form">
        <h4 className="email-section-heading" style={{ fontSize: '1rem' }}>
          Incoming
        </h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="support-inbox-address">Support inbox address</label>
            <input
              id="support-inbox-address"
              type="email"
              value={form.inbox_address}
              onChange={(e) => setForm({ ...form, inbox_address: e.target.value })}
              placeholder="support@yourcompany.com"
              required
            />
            <small>
              Route this address to the Worker (Cloudflare Email Routing) and/or your inbound webhook.
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="support-from-name">Display name</label>
            <input
              id="support-from-name"
              type="text"
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              placeholder="GoBunnyy Support"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="support-inbound-mode">Ingest mode (ops guide)</label>
            <select
              id="support-inbound-mode"
              value={form.inbound.mode}
              onChange={(e) =>
                setForm({
                  ...form,
                  inbound: { ...form.inbound, mode: e.target.value },
                })
              }
            >
              {MODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="support-accept-addresses">Extra accepted To addresses</label>
            <input
              id="support-accept-addresses"
              type="text"
              value={form.inbound.accept_addresses_text}
              onChange={(e) =>
                setForm({
                  ...form,
                  inbound: { ...form.inbound, accept_addresses_text: e.target.value },
                })
              }
              placeholder="help@…, tickets@… (optional, comma-separated)"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="email-toggle-label">
            <input
              type="checkbox"
              checked={form.inbound.reject_unknown_to}
              onChange={(e) =>
                setForm({
                  ...form,
                  inbound: { ...form.inbound, reject_unknown_to: e.target.checked },
                })
              }
            />{' '}
            Reject mail not addressed to configured support addresses
          </label>
        </div>

        <h4 className="email-section-heading" style={{ fontSize: '1rem', marginTop: '1.25rem' }}>
          Outgoing
        </h4>
        <div className="form-group">
          <label className="email-toggle-label">
            <input
              type="checkbox"
              checked={form.outbound.enabled}
              onChange={(e) =>
                setForm({
                  ...form,
                  outbound: { ...form.outbound, enabled: e.target.checked },
                })
              }
            />{' '}
            Enable support customer emails (acks, replies, resolved)
          </label>
          <small>When off, tickets still work; customers will not receive support mail.</small>
        </div>

        <div className="form-group">
          <label className="email-toggle-label">
            <input
              type="checkbox"
              checked={form.outbound.use_dedicated_smtp}
              onChange={(e) =>
                setForm({
                  ...form,
                  outbound: { ...form.outbound, use_dedicated_smtp: e.target.checked },
                })
              }
            />{' '}
            Use dedicated SMTP for support (instead of global SMTP)
          </label>
        </div>

        {dedicated && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>SMTP host</label>
                <input
                  type="text"
                  value={form.outbound.smtp.host}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      outbound: {
                        ...form.outbound,
                        smtp: { ...form.outbound.smtp, host: e.target.value },
                      },
                    })
                  }
                  placeholder="smtp.example.com"
                />
              </div>
              <div className="form-group">
                <label>Port</label>
                <input
                  type="number"
                  min={1}
                  max={65535}
                  value={form.outbound.smtp.port}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      outbound: {
                        ...form.outbound,
                        smtp: {
                          ...form.outbound.smtp,
                          port: parseInt(e.target.value, 10) || 587,
                        },
                      },
                    })
                  }
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Security</label>
                <select
                  value={form.outbound.smtp.secure}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      outbound: {
                        ...form.outbound,
                        smtp: { ...form.outbound.smtp, secure: e.target.value },
                      },
                    })
                  }
                >
                  {SECURE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={form.outbound.smtp.username}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      outbound: {
                        ...form.outbound,
                        smtp: { ...form.outbound.smtp, username: e.target.value },
                      },
                    })
                  }
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Password
                  {data?.outbound?.smtp?.password_set && (
                    <span className="email-key-hint"> (saved — leave blank to keep)</span>
                  )}
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={form.outbound.smtp.password}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      outbound: {
                        ...form.outbound,
                        smtp: { ...form.outbound.smtp, password: e.target.value },
                      },
                    })
                  }
                  placeholder={data?.outbound?.smtp?.password_set ? '••••••••' : 'SMTP password'}
                />
              </div>
              <div className="form-group">
                <label>From email (optional)</label>
                <input
                  type="email"
                  value={form.outbound.smtp.from_email}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      outbound: {
                        ...form.outbound,
                        smtp: { ...form.outbound.smtp, from_email: e.target.value },
                      },
                    })
                  }
                  placeholder="Defaults to inbox address"
                />
              </div>
            </div>
          </>
        )}

        <div className="email-test-panel" style={{ marginTop: '1rem' }}>
          <div className="email-test-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="support-test-to">Send test to</label>
              <input
                id="support-test-to"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <LoadingButton
              type="button"
              className="btn btn-secondary"
              icon={FiSend}
              loading={testMutation.isLoading}
              loadingLabel="Sending…"
              disabled={!testTo?.trim() || !form.outbound.enabled}
              onClick={() => testMutation.mutate()}
            >
              Test support send
            </LoadingButton>
          </div>
        </div>

        <div className="form-actions">
          <LoadingButton
            type="submit"
            className="btn btn-primary"
            icon={FiSave}
            loading={saveMutation.isLoading}
            loadingLabel="Saving…"
          >
            Save support email
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}
